import type { DelayDeliveryLookupInput, DelayDeliveryResult } from '@/types';
import { ApiError } from '@/features/bet-inspection/lib/api';

/**
 * POST {api}/delay-delivery — retry rows for wins/rollbacks that failed to reach
 * the operator. An id filter is resolved through `transactions` server-side, so
 * the caller can pass the id support actually has.
 */
export async function fetchDelayDeliveries(params: {
  api: string;
  token: string | null;
  input: DelayDeliveryLookupInput;
  signal?: AbortSignal;
}): Promise<DelayDeliveryResult> {
  const base = params.api.replace(/\/+$/, '');

  let res: Response;
  try {
    res = await fetch(`${base}/delay-delivery`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(params.token ? { authorization: `Bearer ${params.token}` } : {}),
      },
      body: JSON.stringify(params.input),
      signal: params.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError(`Could not reach the betlens API at ${base}.`, 0);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as DelayDeliveryResult;
}
